<template>
  <div v-if="shouldShow" :class="fontSizeClass" class="leading-none">
    <p v-if="currentStatus" :class="[currentStatus.colorClass, center && 'justify-center']" class="leading-none flex items-end gap-0.5 whitespace-nowrap">
      <u-icon v-if="currentStatus.icon" :class="['leading-none', currentStatus.iconClass, iconSize]" :name="currentStatus.icon"/>

      <span class="leading-none font-semibold">
        <span v-html="longText? currentStatus.text : currentStatus.shortText || currentStatus.text" />
        <a v-if="currentStatus.hasLink" :data-gtmid="currentStatus.linkGTMId" href="#" @click="toAwaitingPayment">{{ currentStatus.linkText }}</a>
      </span>
    </p>
  </div>
</template>

<script setup>
import {escapeHtml, sanitizeUrl} from '~/utils/sanitizers.js';

const props = defineProps({
  project: Object,
  fontSizeClass: {
    type: String,
    default: 'text-xs'
  },
  center: Boolean,
  longText: Boolean
});

function formatDate (date) {
  return date ? useDayjs()(date).format('l') : '';
}

const safeTransferredTo = computed(() => escapeHtml(props.project.transferred_to || ''));

const iconSize = computed(() => {
  switch (props.fontSizeClass) {
    case 'text-base':
    case 'text-lg':
      return 'h-5 w-5';
    case 'text-xl':
      return 'h-6 w-6';
    default:
      return 'h-3.5 w-3.5';
  }
});

const statusMap = computed(() => ({
  permanent: {
    condition: () => props.project.hasStoragePermanent,
    text: props.project.made_permanent_at ? `Saved FOREVER<sub class="reg-mark">&reg;</sub> on ${formatDate(props.project.made_permanent_at)}` : 'Saved FOREVER<sub class="reg-mark">&reg;</sub>',
    icon: 'i-ri-check-double-line',
    colorClass: 'text-sage-800'
  },
  inProgress: {
    condition: () => props.project.hasStorageInProgress,
    text: 'Saving FOREVER<sub class="reg-mark">&reg;</sub>',
    icon: 'i-ri-loader-4-line',
    iconClass: 'animate-spin',
    colorClass: 'text-charcoal-400'
  },
  expired: {
    condition: () => props.project.complete && props.project.download_expired && !props.project.transferred_to,
    text: `Expired on ${formatDate(props.project.download_expires_at)}`,
    icon: 'i-ri-error-warning-fill',
    colorClass: 'text-salmon-500'
  },
  available: {
    condition: () => props.project.complete && !props.project.download_expired,
    text: `Available Until ${formatDate(props.project.download_expires_at)}`,
    colorClass: 'text-salmon-500'
  },
  awaitingPayment: {
    condition: () => props.project.cart_link,
    text: 'Extra Items ',
    linkText: 'Awaiting Payment',
    hasLink: true,
    linkGTMId: 'core_ui-digi-overage_checkout',
    colorClass: 'text-charcoal-400'
  },
  estimated: {
    condition: () => props.project.estimated_completion_date,
    text: `Estimated Completion ${formatDate(props.project.estimated_completion_date)}`,
    colorClass: 'text-charcoal-400'
  },
  giftReturned: {
    condition: () => props.project.gift && props.project.transferred_to,
    text: props.project.transferred_at ? `Gift returned by ${safeTransferredTo.value} on ${formatDate(props.project.transferred_at)}` : `Gift Returned by ${safeTransferredTo.value}`,
    shortText: props.project.transferred_at ? `Gift returned on ${formatDate(props.project.transferred_at)}` : `Gift Returned by ${safeTransferredTo.value}`,
    colorClass: 'text-charcoal-400'
  },
  transferReturned: {
    condition: () => props.project.transferred_to,
    text: props.project.transferred_at ? `Transfer returned by ${safeTransferredTo.value} on ${formatDate(props.project.transferred_at)}` : `Transfer Returned by ${safeTransferredTo.value}`,
    shortText: props.project.transferred_at ? `Transfer returned on ${formatDate(props.project.transferred_at)}` : `Transfer Returned by ${safeTransferredTo.value}`,
    colorClass: 'text-charcoal-400'
  },
  cancelled: {
    condition: () => props.project.cancelled_at,
    text: `Canceled on ${formatDate(props.project.cancelled_at)}`,
    colorClass: 'text-charcoal-400'
  }
}));

const currentStatus = computed(() =>
  Object.values(statusMap.value).find(status => status.condition())
);

const shouldShow = computed(() => Boolean(currentStatus.value));

function toAwaitingPayment () {
  const safeUrl = sanitizeUrl(props.project.cart_link);

  if (!safeUrl) {
    useErrorToast().add('Unable to open the payment link.');
    return;
  }

  window.location = safeUrl;
}
</script>
